import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  BadgeCheck,
  ShieldCheck,
  Phone,
  Mail,
  Globe2,
  Users,
  Target,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { firestore } from '@config/firebase';
import { useAuth } from '@context/AuthContext';
import type { BusinessType } from '../../types/landlord.types';

type StepId = 'company' | 'portfolio' | 'compliance' | 'review';

interface Step {
  id: StepId;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface LandlordFormData {
  businessName: string;
  businessType: BusinessType;
  contactName: string;
  phone: string;
  email: string;
  websiteUrl: string;
  portfolioSize: number | null;
  regions: string;
  listingGoal: string;
  bio: string;
}

const steps: Step[] = [
  {
    id: 'company',
    title: 'Business basics',
    description: 'Who are you and how do we reach you?',
    icon: <Building2 className="w-4 h-4" />,
  },
  {
    id: 'portfolio',
    title: 'Portfolio & reach',
    description: 'Where do you operate and what do you list?',
    icon: <Users className="w-4 h-4" />,
  },
  {
    id: 'compliance',
    title: 'Trust & compliance',
    description: 'Transparency to keep tenants confident.',
    icon: <ShieldCheck className="w-4 h-4" />,
  },
  {
    id: 'review',
    title: 'Review & launch',
    description: 'Lock it in and head to your dashboard.',
    icon: <BadgeCheck className="w-4 h-4" />,
  },
];

const businessTypes: { label: string; value: BusinessType }[] = [
  { label: 'Individual landlord', value: 'individual' },
  { label: 'Agency', value: 'agency' },
  { label: 'Property management', value: 'property-management' },
];

const LandlordOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, refreshProfile } = useAuth();

  const [currentStep, setCurrentStep] = useState<StepId>('company');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [existingCreatedAt, setExistingCreatedAt] = useState<any>(null);
  const [formData, setFormData] = useState<LandlordFormData>({
    businessName: '',
    businessType: 'individual',
    contactName: '',
    phone: '',
    email: currentUser?.email || '',
    websiteUrl: '',
    portfolioSize: null,
    regions: '',
    listingGoal: '',
    bio: '',
  });

  const stepIndex = useMemo(() => steps.findIndex((step) => step.id === currentStep), [currentStep]);
  const completion = useMemo(() => Math.round(((stepIndex + (submitted ? 1 : 0)) / (steps.length - 1)) * 100), [stepIndex, submitted]);

  useEffect(() => {
    async function loadExistingProfile() {
      if (!currentUser) return;
      const ref = doc(firestore, 'landlords', currentUser.uid);
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        const data = snapshot.data();
        setExistingCreatedAt(data.createdAt || null);
        setFormData((prev) => ({
          ...prev,
          businessName: data.businessName || '',
          businessType: data.businessType || 'individual',
          contactName: data.contactName || data.businessName || '',
          phone: data.phone || '',
          email: data.email || currentUser.email || '',
          websiteUrl: data.websiteUrl || '',
          portfolioSize: data.portfolioSize ?? null,
          regions: data.regions || '',
          listingGoal: data.listingGoal || '',
          bio: data.bio || '',
        }));
      } else if (currentUser.email) {
        setFormData((prev) => ({ ...prev, email: currentUser.email || '' }));
      }
    }

    loadExistingProfile();
  }, [currentUser]);

  const handleChange = <K extends keyof LandlordFormData>(key: K, value: LandlordFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const isStepComplete = (id: StepId) => {
    if (id === 'company') {
      return Boolean(formData.businessName.trim() && formData.contactName.trim() && formData.phone.trim());
    }
    if (id === 'portfolio') {
      return Boolean(formData.regions.trim());
    }
    return true;
  };

  const saveProfile = async (markComplete = false) => {
    if (!currentUser) {
      throw new Error('No signed-in landlord.');
    }
    setSaving(true);
    try {
      await setDoc(
        doc(firestore, 'landlords', currentUser.uid),
        {
          id: currentUser.uid,
          email: formData.email || currentUser.email || '',
          role: 'landlord',
          businessName: formData.businessName.trim(),
          businessType: formData.businessType,
          contactName: formData.contactName.trim(),
          phone: formData.phone.trim(),
          websiteUrl: formData.websiteUrl.trim() || null,
          portfolioSize: formData.portfolioSize ?? 0,
          regions: formData.regions.trim(),
          listingGoal: formData.listingGoal.trim(),
          bio: formData.bio.trim(),
          isVerified: false,
          verificationDocuments: [],
          activeListings: 0,
          totalInquiries: 0,
          // Only flip to complete on the final step.
          ...(markComplete ? { profileComplete: true } : {}),
          createdAt: existingCreatedAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      // Keep auth context in sync so route guards see the completed profile.
      await refreshProfile();
      if (markComplete) setSubmitted(true);
    } catch (error) {
      console.error('Error saving landlord profile:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const onNext = async () => {
    setSaveError(null);
    try {
      if (currentStep === 'review') {
        await saveProfile(true);
        navigate('/landlord/dashboard');
        return;
      }
      await saveProfile(false);
      const next = steps[stepIndex + 1];
      if (next) setCurrentStep(next.id);
    } catch {
      setSaveError('We could not save your details. Please check your connection and try again.');
    }
  };

  const onBack = () => {
    const prev = steps[stepIndex - 1];
    if (prev) setCurrentStep(prev.id);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="card p-6 max-w-md text-center space-y-3">
          <ShieldCheck className="w-8 h-8 text-primary-300 mx-auto" />
          <h1 className="text-2xl font-bold text-text-primary">Sign in to continue</h1>
          <p className="text-text-secondary">Landlord onboarding requires an authenticated account.</p>
          <button onClick={() => navigate('/login')} className="btn-primary w-full">Go to login</button>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'company':
        return (
          <div className="card p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lo-business-name" className="block text-sm font-semibold text-text-primary mb-2">Business name</label>
                <input
                  id="lo-business-name"
                  className="input"
                  value={formData.businessName}
                  onChange={(e) => handleChange('businessName', e.target.value)}
                  placeholder="e.g. Cape Rentals"
                />
              </div>
              <div>
                <span className="block text-sm font-semibold text-text-primary mb-2">Business type</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {businessTypes.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChange('businessType', option.value)}
                      className={`px-3 py-2 rounded-xl border-2 text-sm font-semibold transition-all duration-200 ${
                        formData.businessType === option.value
                          ? 'border-primary-300 bg-primary-50 text-primary-300 shadow-sm'
                          : 'border-gray-200 text-text-secondary hover:border-primary-100'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="lo-contact-name" className="block text-sm font-semibold text-text-primary mb-2">Primary contact</label>
                <input
                  id="lo-contact-name"
                  className="input"
                  value={formData.contactName}
                  onChange={(e) => handleChange('contactName', e.target.value)}
                  placeholder="Who should tenants see?"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="lo-phone" className="block text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary-300" /> Phone
                  </label>
                  <input
                    id="lo-phone"
                    type="tel"
                    className="input"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+27 82 123 4567"
                  />
                </div>
                <div>
                  <label htmlFor="lo-email" className="block text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary-300" /> Contact email
                  </label>
                  <input
                    id="lo-email"
                    type="email"
                    className="input"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="you@business.com"
                  />
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border-2 border-primary-100 bg-primary-50/60">
                <div className="flex items-center text-primary-300 font-semibold mb-2">
                  <BadgeCheck className="w-4 h-4 mr-2" /> Credibility
                </div>
                <p className="text-sm text-text-secondary">Clear contact details keep response rates high.</p>
              </div>
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4 mr-2 text-primary-300" /> Transparency
                </div>
                <p className="text-sm text-text-secondary">Tenants see who they are talking to before they book a viewing.</p>
              </div>
              <div className="p-4 rounded-xl border-2 border-accent-purple/40 bg-accent-purple/10">
                <div className="flex items-center text-accent-purple font-semibold mb-2">
                  <Users className="w-4 h-4 mr-2" /> Team friendly
                </div>
                <p className="text-sm text-text-secondary">Add a generic inbox and forward inquiries internally.</p>
              </div>
            </div>
          </div>
        );
      case 'portfolio':
        return (
          <div className="card p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lo-regions" className="block text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                  <Globe2 className="w-4 h-4 text-primary-300" /> Regions you cover
                </label>
                <textarea
                  id="lo-regions"
                  className="input min-h-[100px]"
                  value={formData.regions}
                  onChange={(e) => handleChange('regions', e.target.value)}
                  placeholder="Cape Town CBD, Sea Point, Gardens..."
                />
                <p className="text-xs text-text-secondary mt-1">We prioritise leads in these areas first.</p>
              </div>
              <div>
                <label htmlFor="lo-listing-goal" className="block text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary-300" /> Listing focus
                </label>
                <input
                  id="lo-listing-goal"
                  className="input"
                  value={formData.listingGoal}
                  onChange={(e) => handleChange('listingGoal', e.target.value)}
                  placeholder="e.g. Student-friendly studios, co-living houses"
                />
              </div>
              <div>
                <label htmlFor="lo-portfolio-size" className="block text-sm font-semibold text-text-primary mb-2">Portfolio size</label>
                <input
                  id="lo-portfolio-size"
                  type="number"
                  min={0}
                  className="input"
                  value={formData.portfolioSize ?? ''}
                  onChange={(e) => handleChange('portfolioSize', e.target.value === '' ? null : Number(e.target.value))}
                  placeholder="Number of active units"
                />
              </div>
              <div>
                <label htmlFor="lo-website" className="block text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
                  <Globe2 className="w-4 h-4 text-primary-300" /> Website (optional)
                </label>
                <input
                  id="lo-website"
                  type="url"
                  className="input"
                  value={formData.websiteUrl}
                  onChange={(e) => handleChange('websiteUrl', e.target.value)}
                  placeholder="https://your-site.com"
                />
              </div>
            </div>
            <div className="p-4 rounded-xl border-2 border-gray-200 bg-white">
              <div className="flex items-center text-text-primary font-semibold mb-2">
                <Users className="w-4 h-4 mr-2 text-primary-300" />
                Lead quality tips
              </div>
              <ul className="text-sm text-text-secondary space-y-1">
                <li>• Specific regions improve search placement.</li>
                <li>• Listing focus helps us route the right tenants.</li>
                <li>• Website links boost trust for first messages.</li>
              </ul>
            </div>
          </div>
        );
      case 'compliance':
        return (
          <div className="card p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lo-bio" className="block text-sm font-semibold text-text-primary mb-2">Short intro for tenants</label>
                <textarea
                  id="lo-bio"
                  className="input min-h-[120px]"
                  value={formData.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  placeholder="Share how you manage viewings, response times, and what you expect from tenants."
                />
              </div>
              <div className="p-4 rounded-xl border-2 border-primary-100 bg-primary-50/60">
                <div className="flex items-center text-primary-300 font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Trust signal
                </div>
                <p className="text-sm text-text-secondary">
                  We’ll mark you as “profile complete” once this step is done. Document verification is optional later.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="p-3 rounded-xl border-2 border-gray-200 bg-white">
                <p className="text-sm font-semibold text-text-primary mb-1">Clear expectations</p>
                <p className="text-xs text-text-secondary">Tell tenants how you handle maintenance and notices.</p>
              </div>
              <div className="p-3 rounded-xl border-2 border-gray-200 bg-white">
                <p className="text-sm font-semibold text-text-primary mb-1">Response time</p>
                <p className="text-xs text-text-secondary">Add business hours so inquiries land at the right time.</p>
              </div>
              <div className="p-3 rounded-xl border-2 border-gray-200 bg-white">
                <p className="text-sm font-semibold text-text-primary mb-1">Verification ready</p>
                <p className="text-xs text-text-secondary">Upload documents later; we’ll keep the badge pending.</p>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-text-muted font-semibold">Review</p>
                <h2 className="text-2xl font-extrabold text-text-primary">Ready to activate your landlord profile</h2>
              </div>
              <div className="px-4 py-2 rounded-full bg-primary-200 text-white font-semibold shadow-lg flex items-center">
                <ShieldCheck className="w-4 h-4 mr-2" /> Profile complete
              </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white/80">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <Building2 className="w-4 h-4 mr-2 text-primary-300" />
                  Business details
                </div>
                <p className="text-text-primary font-semibold">{formData.businessName || 'Add business name'}</p>
                <p className="text-text-secondary text-sm capitalize">{formData.businessType.replace('-', ' ')}</p>
                <p className="text-text-secondary text-sm mt-2">{formData.contactName}</p>
                <p className="text-text-secondary text-sm">{formData.phone}</p>
              </div>
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white/80">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <Users className="w-4 h-4 mr-2 text-primary-300" />
                  Coverage & focus
                </div>
                <p className="text-text-primary font-semibold">{formData.regions || 'Add regions'}</p>
                <p className="text-text-secondary text-sm">{formData.listingGoal || 'Add your listing focus'}</p>
                {formData.portfolioSize !== null && (
                  <p className="text-text-secondary text-sm mt-1">Portfolio size: {formData.portfolioSize}</p>
                )}
              </div>
              <div className="p-4 rounded-xl border-2 border-gray-200 bg-white/80">
                <div className="flex items-center text-text-primary font-semibold mb-2">
                  <BadgeCheck className="w-4 h-4 mr-2 text-primary-300" />
                  Tenant-facing intro
                </div>
                <p className="text-text-secondary text-sm leading-relaxed">
                  {formData.bio || 'Share how you run viewings, respond to inquiries, and keep things transparent.'}
                </p>
              </div>
            </div>
            {submitted && (
              <div className="p-3 md:p-4 rounded-xl bg-green-50 border-2 border-semantic-success text-semantic-success text-sm md:text-base font-semibold flex items-center">
                <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
                Saved. Head to your dashboard to create listings.
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-purple/10 px-4 py-10">
      <div className="relative max-w-6xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs md:text-sm uppercase tracking-wide text-text-muted font-semibold">Landlord onboarding</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-text-primary">Set up your landlord profile</h1>
            <p className="text-sm md:text-base text-text-secondary mt-2">
              Share who you are, where you operate, and how tenants should work with you. No tenant fields mixed in.
            </p>
          </div>
          <div className="px-3 py-2 md:px-5 md:py-3 rounded-2xl bg-white border-2 border-primary-100 shadow-lg text-primary-300 text-xs md:text-sm font-semibold flex items-center gap-1.5 md:gap-2">
            <BadgeCheck className="w-4 h-4 md:w-5 md:h-5" /> <span>Business-ready profile</span>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-text-secondary">Progress</span>
            <span className="text-sm font-bold text-text-primary">{completion}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 bg-gradient-to-r from-primary-200 via-primary-300 to-accent-purple rounded-full"
              style={{ width: `${Math.min(completion, 100)}%` }}
            ></div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-4">
            <div className="card p-5 space-y-4">
              {steps.map((step, index) => {
                const complete = index < stepIndex || (step.id === currentStep && submitted);
                const active = step.id === currentStep;
                return (
                  <div
                    key={step.id}
                    className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-200 ${
                      active ? 'bg-primary-50 border border-primary-100 shadow-sm' : 'border border-gray-100'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                        complete || active
                          ? 'bg-gradient-to-br from-primary-200 to-primary-300 text-white border-primary-300'
                          : 'bg-white text-text-secondary border-gray-200'
                      }`}
                    >
                      {complete ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-text-primary font-semibold">
                        {step.icon}
                        {step.title}
                      </div>
                      <p className="text-sm text-text-secondary">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="card p-5 space-y-3">
              <div className="flex items-center text-primary-300 font-semibold">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Lead quality promise
              </div>
              <p className="text-sm text-text-secondary">
                Complete onboarding to unlock inquiry routing, saved replies, and better placement in tenant searches.
              </p>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <CheckCircle2 className="w-4 h-4 text-primary-300" />
                No tenant settings are collected here.
              </div>
            </div>
          </aside>

          <section className="lg:col-span-8 space-y-4">
            {renderStepContent()}
            {saveError && (
              <div role="alert" className="p-4 rounded-xl border-2 bg-red-50 border-red-200 text-red-700 text-sm">
                {saveError}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {stepIndex > 0 && (
                <button type="button" onClick={onBack} className="btn-secondary text-sm md:text-base px-3 py-2 md:px-4 md:py-2">
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={onNext}
                disabled={saving || !isStepComplete(currentStep)}
                className="btn-primary text-sm md:text-base px-3 py-2 md:px-4 md:py-2 inline-flex items-center gap-2"
              >
                {currentStep === 'review' ? 'Save & go to dashboard' : 'Next step'}
                <ArrowRight className="w-4 h-4" />
              </button>
              {saving && <span className="text-xs md:text-sm text-text-secondary">Saving…</span>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LandlordOnboarding;
